import React, { useState, useEffect } from 'react';
import { 
    Calendar, TrendingUp, TrendingDown, Share2, 
    Download, Lightbulb, ChevronLeft, ChevronRight, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Legend
} from 'recharts';
import MobileLayout from '../components/layout/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';
import { exportDailyReport, exportMonthlyReport } from '../lib/pdfExport';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

// Custom legend to ensure Sales appears before Expenses
const CustomChartLegend = ({ language }) => (
    <div className="flex justify-center gap-4 mt-2 text-xs">
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10B981' }} />
            <span>{language === 'en' ? 'Sales' : 'बिक्री'}</span>
        </div>
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E11D48' }} />
            <span>{language === 'en' ? 'Expenses' : 'खर्च'}</span>
        </div>
    </div>
);

const Reports = () => {
    const { authAxios } = useAuth();
    const { t, language } = useLanguage();
    
    const [activeTab, setActiveTab] = useState('daily');
    const [dailyReport, setDailyReport] = useState(null);
    const [monthlyReport, setMonthlyReport] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [loading, setLoading] = useState(true);

    const fetchDailyReport = async () => {
        setLoading(true);
        try {
            const response = await authAxios.get(`/reports/daily?date=${selectedDate}`);
            setDailyReport(response.data);
        } catch (err) {
            console.error('Fetch daily report error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyReport = async () => {
        setLoading(true);
        try {
            const response = await authAxios.get(`/reports/monthly?month=${selectedMonth}`);
            setMonthlyReport(response.data);
        } catch (err) {
            console.error('Fetch monthly report error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'daily') {
            fetchDailyReport();
        } else {
            fetchMonthlyReport();
        }
    }, [activeTab, selectedDate, selectedMonth]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatCompactCurrency = (value) => {
        if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
        if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
        return `₹${value}`;
    };

    const navigateDate = (direction) => {
        const date = new Date(selectedDate);
        date.setDate(date.getDate() + direction);
        setSelectedDate(date.toISOString().split('T')[0]);
    };

    const navigateMonth = (direction) => {
        const [year, month] = selectedMonth.split('-').map(Number);
        const newDate = new Date(year, month - 1 + direction, 1);
        setSelectedMonth(newDate.toISOString().slice(0, 7));
    };

    const shareOnWhatsApp = () => {
        const report = activeTab === 'daily' ? dailyReport : monthlyReport;
        if (!report) return;

        // Use text symbols instead of emojis for better URL encoding
        const message = activeTab === 'daily'
            ? `*Daily Report - ${selectedDate}*

Sales: ${formatCurrency(report.total_sales)}
Expenses: ${formatCurrency(report.total_expenses)}
${report.profit >= 0 ? 'Profit' : 'Loss'}: ${formatCurrency(Math.abs(report.profit))}
Transactions: ${report.transaction_count}

- ShieldPayments`
            : `*Monthly Report - ${selectedMonth}*

Total Sales: ${formatCurrency(report.total_sales)}
Total Expenses: ${formatCurrency(report.total_expenses)}
${report.profit >= 0 ? 'Profit' : 'Loss'}: ${formatCurrency(Math.abs(report.profit))}
Transactions: ${report.transaction_count}

- ShieldPayments`;

        const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    // Prepare category data for daily pie chart
    const prepareCategoryChartData = () => {
        if (!dailyReport?.categories) return [];
        
        return Object.entries(dailyReport.categories)
            .map(([name, data]) => ({
                name,
                value: data.sales + data.expenses,
                sales: data.sales,
                expenses: data.expenses,
                count: data.count
            }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);
    };

    // Prepare daily breakdown data for monthly bar chart
    const prepareMonthlyChartData = () => {
        if (!monthlyReport?.daily_breakdown) return [];
        
        return Object.entries(monthlyReport.daily_breakdown)
            .map(([date, data]) => ({
                date: date.slice(8), // Just the day number
                fullDate: date,
                sales: data.sales,
                expenses: data.expenses,
                profit: data.sales - data.expenses
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
    };

    // Custom tooltip for pie chart
    const CustomPieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-background border border-border rounded-lg p-2 shadow-lg">
                    <p className="font-medium">{data.name}</p>
                    {data.sales > 0 && <p className="text-emerald-600 text-sm">Sales: {formatCurrency(data.sales)}</p>}
                    {data.expenses > 0 && <p className="text-rose-600 text-sm">Expenses: {formatCurrency(data.expenses)}</p>}
                    <p className="text-muted-foreground text-xs">{data.count} transactions</p>
                </div>
            );
        }
        return null;
    };

    // Custom tooltip for bar chart
    const CustomBarTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            return (
                <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
                    <p className="font-medium text-sm mb-1">{data?.fullDate}</p>
                    <p className="text-emerald-600 text-sm">Sales: {formatCurrency(data?.sales || 0)}</p>
                    <p className="text-rose-600 text-sm">Expenses: {formatCurrency(data?.expenses || 0)}</p>
                    <p className={cn(
                        "text-sm font-medium mt-1",
                        (data?.profit || 0) >= 0 ? "text-blue-600" : "text-amber-600"
                    )}>
                        Profit: {formatCurrency(data?.profit || 0)}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <MobileLayout>
            <div className="space-y-4" data-testid="reports-page">
                {/* Header */}
                <h1 className="text-2xl font-bold font-heading">{t('reports')}</h1>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="daily" data-testid="tab-daily">
                            {t('dailyReport')}
                        </TabsTrigger>
                        <TabsTrigger value="monthly" data-testid="tab-monthly">
                            {t('monthlyReport')}
                        </TabsTrigger>
                    </TabsList>

                    {/* Daily Report */}
                    <TabsContent value="daily" className="mt-4 space-y-4">
                        {/* Date Selector */}
                        <div className="flex items-center justify-between">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigateDate(-1)}
                                data-testid="prev-date-btn"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-muted-foreground" />
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="bg-transparent border-none text-center font-medium"
                                    data-testid="date-picker"
                                />
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigateDate(1)}
                                disabled={selectedDate === new Date().toISOString().split('T')[0]}
                                data-testid="next-date-btn"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="space-y-4">
                                <Skeleton className="h-32" />
                                <Skeleton className="h-48" />
                            </div>
                        ) : dailyReport && (
                            <>
                                {/* Summary Cards */}
                                <div className="grid grid-cols-3 gap-3">
                                    <Card className="bg-emerald-50 dark:bg-emerald-900/20">
                                        <CardContent className="p-3 text-center">
                                            <TrendingUp className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
                                            <p className="text-xs text-muted-foreground">{t('sale')}</p>
                                            <p className="font-bold font-number text-emerald-600">
                                                {formatCurrency(dailyReport.total_sales)}
                                            </p>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-rose-50 dark:bg-rose-900/20">
                                        <CardContent className="p-3 text-center">
                                            <TrendingDown className="w-5 h-5 mx-auto text-rose-500 mb-1" />
                                            <p className="text-xs text-muted-foreground">{t('expense')}</p>
                                            <p className="font-bold font-number text-rose-600">
                                                {formatCurrency(dailyReport.total_expenses)}
                                            </p>
                                        </CardContent>
                                    </Card>
                                    <Card className={cn(
                                        dailyReport.profit >= 0 
                                            ? "bg-blue-50 dark:bg-blue-900/20"
                                            : "bg-amber-50 dark:bg-amber-900/20"
                                    )}>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-xs text-muted-foreground">
                                                {dailyReport.profit >= 0 ? t('profit') : t('loss')}
                                            </p>
                                            <p className={cn(
                                                "font-bold font-number text-xl",
                                                dailyReport.profit >= 0 ? "text-blue-600" : "text-amber-600"
                                            )}>
                                                {formatCurrency(Math.abs(dailyReport.profit))}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Daily Category Chart */}
                                {prepareCategoryChartData().length > 0 && (
                                    <Card>
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base">
                                                {language === 'en' ? 'Sales by Category' : 'श्रेणी के अनुसार बिक्री'}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart 
                                                    data={prepareCategoryChartData()} 
                                                    layout="vertical"
                                                    margin={{ left: 10, right: 10 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                                    <XAxis 
                                                        type="number" 
                                                        tick={{ fontSize: 10 }}
                                                        tickFormatter={(value) => formatCompactCurrency(value)}
                                                        axisLine={false}
                                                        tickLine={false}
                                                    />
                                                    <YAxis 
                                                        type="category" 
                                                        dataKey="name" 
                                                        tick={{ fontSize: 11 }}
                                                        width={80}
                                                        axisLine={false}
                                                        tickLine={false}
                                                    />
                                                    <Tooltip content={<CustomPieTooltip />} />
                                                    <Bar 
                                                        dataKey="sales" 
                                                        fill="#10B981" 
                                                        radius={[0, 4, 4, 0]}
                                                        name={language === 'en' ? 'Sales' : 'बिक्री'}
                                                    />
                                                    <Bar 
                                                        dataKey="expenses" 
                                                        fill="#E11D48" 
                                                        radius={[0, 4, 4, 0]}
                                                        name={language === 'en' ? 'Expenses' : 'खर्च'}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Categories Breakdown List */}
                                {Object.keys(dailyReport.categories || {}).length > 0 && (
                                    <Card>
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base">
                                                {language === 'en' ? 'Category Details' : 'श्रेणी विवरण'}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-2">
                                            {Object.entries(dailyReport.categories).map(([cat, data], index) => (
                                                <div key={cat} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <div 
                                                            className="w-3 h-3 rounded-full" 
                                                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                                        />
                                                        <span className="font-medium">{cat}</span>
                                                        <span className="text-xs text-muted-foreground">({data.count})</span>
                                                    </div>
                                                    <div className="text-right text-sm">
                                                        {data.sales > 0 && (
                                                            <span className="text-emerald-600">+{formatCurrency(data.sales)}</span>
                                                        )}
                                                        {data.expenses > 0 && (
                                                            <span className="text-rose-600 ml-2">-{formatCurrency(data.expenses)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* No Data State */}
                                {dailyReport.transaction_count === 0 && (
                                    <Card className="bg-muted/30">
                                        <CardContent className="p-8 text-center">
                                            <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                                            <p className="text-muted-foreground">
                                                {language === 'en' ? 'No transactions on this day' : 'इस दिन कोई लेन-देन नहीं'}
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Transaction Count */}
                                {dailyReport.transaction_count > 0 && (
                                    <div className="text-center text-muted-foreground">
                                        {dailyReport.transaction_count} {language === 'en' ? 'transactions' : 'लेन-देन'}
                                    </div>
                                )}
                            </>
                        )}
                    </TabsContent>

                    {/* Monthly Report */}
                    <TabsContent value="monthly" className="mt-4 space-y-4">
                        {/* Month Selector */}
                        <div className="flex items-center justify-between">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigateMonth(-1)}
                                data-testid="prev-month-btn"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-muted-foreground" />
                                <input
                                    type="month"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="bg-transparent border-none text-center font-medium"
                                    data-testid="month-picker"
                                />
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigateMonth(1)}
                                disabled={selectedMonth === new Date().toISOString().slice(0, 7)}
                                data-testid="next-month-btn"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                        </div>

                        {loading ? (
                            <div className="space-y-4">
                                <Skeleton className="h-32" />
                                <Skeleton className="h-64" />
                            </div>
                        ) : monthlyReport && (
                            <>
                                {/* Summary */}
                                <Card className={cn(
                                    "border-2",
                                    monthlyReport.profit >= 0 
                                        ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                                        : "border-rose-500 bg-rose-50 dark:bg-rose-900/20"
                                )}>
                                    <CardContent className="p-4 text-center">
                                        <p className="text-sm text-muted-foreground">
                                            {language === 'en' ? 'Monthly' : 'मासिक'} {monthlyReport.profit >= 0 ? t('profit') : t('loss')}
                                        </p>
                                        <p className={cn(
                                            "text-4xl font-bold font-number",
                                            monthlyReport.profit >= 0 ? "text-emerald-600" : "text-rose-600"
                                        )}>
                                            {formatCurrency(Math.abs(monthlyReport.profit))}
                                        </p>
                                        <div className="flex justify-center gap-6 mt-3 text-sm">
                                            <span className="text-emerald-600">
                                                ↑ {formatCurrency(monthlyReport.total_sales)}
                                            </span>
                                            <span className="text-rose-600">
                                                ↓ {formatCurrency(monthlyReport.total_expenses)}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Daily Trend Chart */}
                                {prepareMonthlyChartData().length > 0 && (
                                    <Card>
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base">
                                                {language === 'en' ? 'Daily Breakdown' : 'दैनिक विवरण'}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ResponsiveContainer width="100%" height={220}>
                                                <BarChart 
                                                    data={prepareMonthlyChartData()} 
                                                    barCategoryGap="15%"
                                                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis 
                                                        dataKey="date" 
                                                        tick={{ fontSize: 10 }} 
                                                        axisLine={false}
                                                        tickLine={false}
                                                    />
                                                    <YAxis 
                                                        tick={{ fontSize: 10 }} 
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickFormatter={(value) => {
                                                            if (value >= 1000) return `${value/1000}k`;
                                                            return value;
                                                        }}
                                                    />
                                                    <Tooltip content={<CustomBarTooltip />} />
                                                    <Bar 
                                                        dataKey="sales" 
                                                        fill="#10B981" 
                                                        radius={[4, 4, 0, 0]}
                                                        name={language === 'en' ? 'Sales' : 'बिक्री'}
                                                        minPointSize={5}
                                                    />
                                                    <Bar 
                                                        dataKey="expenses" 
                                                        fill="#E11D48" 
                                                        radius={[4, 4, 0, 0]}
                                                        name={language === 'en' ? 'Expenses' : 'खर्च'}
                                                        minPointSize={3}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                            <CustomChartLegend language={language} />
                                            
                                            {/* Daily Details Table */}
                                            <div className="mt-4 space-y-1 max-h-40 overflow-y-auto">
                                                {prepareMonthlyChartData().map((day) => (
                                                    <div 
                                                        key={day.fullDate}
                                                        className="flex items-center justify-between text-sm p-2 rounded hover:bg-muted/50"
                                                    >
                                                        <span className="text-muted-foreground">{day.fullDate}</span>
                                                        <div className="flex gap-4">
                                                            <span className="text-emerald-600">+{formatCurrency(day.sales)}</span>
                                                            {day.expenses > 0 && (
                                                                <span className="text-rose-600">-{formatCurrency(day.expenses)}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Savings Tips */}
                                {(monthlyReport.savings_tips?.en?.length > 0 || monthlyReport.savings_tips?.hi?.length > 0) && (
                                    <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <Lightbulb className="w-5 h-5 text-amber-500" />
                                                {language === 'en' ? 'Tips' : 'सुझाव'}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-2">
                                            {(language === 'en' ? monthlyReport.savings_tips.en : monthlyReport.savings_tips.hi).map((tip, idx) => (
                                                <p key={idx} className="text-sm text-amber-700 dark:text-amber-300">
                                                    • {tip}
                                                </p>
                                            ))}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Transaction Count */}
                                <div className="text-center text-muted-foreground">
                                    {monthlyReport.transaction_count} {language === 'en' ? 'transactions this month' : 'इस महीने लेन-देन'}
                                </div>
                            </>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Export Buttons */}
                <div className="flex gap-3">
                    <Button
                        className="flex-1 h-12"
                        variant="outline"
                        onClick={async () => {
                            console.log('Export PDF clicked');
                            
                            // Simple test - create a basic PDF
                            try {
                                const { default: jsPDF } = await import('jspdf');
                                const doc = new jsPDF();
                                
                                // Header
                                doc.setFillColor(59, 130, 246);
                                doc.rect(0, 0, 210, 30, 'F');
                                doc.setTextColor(255, 255, 255);
                                doc.setFontSize(20);
                                doc.text('ShieldPayments Report', 15, 20);
                                
                                // Content
                                doc.setTextColor(0, 0, 0);
                                doc.setFontSize(12);
                                
                                const reportData = activeTab === 'daily' ? dailyReport : monthlyReport;
                                
                                if (reportData) {
                                    doc.text(`Report Type: ${activeTab === 'daily' ? 'Daily' : 'Monthly'}`, 15, 45);
                                    doc.text(`Total Sales: Rs. ${(reportData.total_sales || 0).toLocaleString()}`, 15, 55);
                                    doc.text(`Total Expenses: Rs. ${(reportData.total_expenses || 0).toLocaleString()}`, 15, 65);
                                    doc.text(`Net Profit: Rs. ${(reportData.profit || 0).toLocaleString()}`, 15, 75);
                                    doc.text(`Transactions: ${reportData.transaction_count || 0}`, 15, 85);
                                    doc.text(`Generated: ${new Date().toLocaleString()}`, 15, 100);
                                } else {
                                    doc.text('No report data available', 15, 45);
                                }
                                
                                // Footer
                                doc.setFontSize(8);
                                doc.text('Generated by ShieldPayments', 15, 285);
                                
                                // Save
                                doc.save(`ShieldPayments_${activeTab}_report.pdf`);
                                console.log('PDF saved successfully');
                            } catch (err) {
                                console.error('PDF Error:', err);
                                alert('Error creating PDF: ' + err.message);
                            }
                        }}
                        data-testid="export-pdf-btn"
                    >
                        <FileText className="w-5 h-5 mr-2" />
                        {language === 'en' ? 'Export PDF' : 'PDF डाउनलोड'}
                    </Button>
                    <Button
                        className="flex-1 h-12 bg-green-500 hover:bg-green-600"
                        onClick={shareOnWhatsApp}
                        data-testid="share-whatsapp-btn"
                    >
                        <Share2 className="w-5 h-5 mr-2" />
                        {t('shareWhatsApp')}
                    </Button>
                </div>
            </div>
        </MobileLayout>
    );
};

export default Reports;
